/* @flow */
import React from 'react';
import { connect } from 'react-redux';

import TriggeredItem, { TriggeredItemConditions } from 'src/data/TriggeredItem';
import { ItemsServiceHelper } from 'src/store/scenario/items';
import HelpButton from 'src/pages/components/HelpButton';
import { EventsServiceHelper, NotificationTypes } from 'src/store/events';

import { InputBoolean } from 'src/pages/components';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type TriggeredItemInputProps = {
  triggeredItem: any,
  items: any,
  triggers: any,
  locale: string,
  updateTrigger: ItemsServiceHelper.updateTriggeredItemDataType,
  parentId: string,
  childId: string,
  nodeId: string,
  screenplayEngineParent: any, // TODO
  screenplayEngineChild: any, // TODO
  childType: string,
  parentType: string,
  scenarioId: string,
  addNotif: EventsServiceHelper.addNotifType,
  startEditing: () => any,
  isEditingItem: boolean,
};

type State = {
  id: string,
  timer?: string,
  condition: string,
  conditionValue?: string,
  newTriggeredCondition?: string,
  newTriggeredConditionValue?: string,
  isValid: boolean,
  disableNotif: boolean,
  hasChanges: boolean,
};

class TriggeredItemInput extends React.PureComponent<TriggeredItemInputProps, State> {
  state = {
    id: '',
    timer: undefined,
    condition: '',
    conditionValue: undefined,
    newTriggeredCondition: undefined,
    newTriggeredConditionValue: undefined,
    isValid: false,
    disableNotif: false,
    hasChanges: false,
  };

  componentDidMount() {
    this.setItemData(this.props);
  }

  componentDidUpdate(oldProps: TriggeredItemInputProps, oldState: State) {
    if (oldProps.nodeId !== this.props.nodeId) {
      this.warnSaveIfNeeded(oldProps, oldState);
    }
    try {
      if (oldProps.nodeId !== this.props.nodeId) {
        this.setItemData(this.props);
      }
    } catch (error) {
      console.debug(error);
    }
  }

  componentWillUnmount() {
    this.warnSaveIfNeeded();
  }

  warnSaveIfNeeded = (props?: TriggeredItemInputProps = this.props, state?: State = this.state) => {
    const { t } = this.props;
    if (this.state.hasChanges && props.scenarioId) {
      const updateData = this.getDataToSave(props, state);
      const { isValid } = state;
      if (isValid) {
        this.props.addNotif(NotificationTypes.WARN, 'W_UNSAVED_ITEM', undefined, 0, {
          title: t(['general.save', '']),
          callback: async () => {
            if (this.props.scenarioId) {
              this.updateWithData(updateData, false);
            } else {
              this.props.addNotif(NotificationTypes.ERROR, 'E_UNSAVED_ITEM', undefined, 0);
            }
          },
          closeOnCallback: true,
        });
      } else {
        this.props.addNotif(NotificationTypes.ERROR, 'E_UNSAVED_ITEM_INVALID', undefined, 0);
      }
    }
  };

  setItemData = (props: TriggeredItemInputProps) => {
    const { triggeredItem } = props;
    if (triggeredItem) {
      const {
        id, condition, newTriggeredCondition, disableNotif,
      } = triggeredItem;
      const newState = {
        id: id || '',
        condition: condition || '',
        newTriggeredCondition: newTriggeredCondition || TriggeredItemConditions.Added,
        hasChanges: false,
        conditionValue: undefined,
        newTriggeredConditionValue: undefined,
        timer: undefined,
        disableNotif: !!disableNotif,
      };
      newState.conditionValue = `${triggeredItem.conditionValue}`;
      if (triggeredItem.conditionValue === undefined) {
        newState.conditionValue = '';
      }
      newState.newTriggeredConditionValue = `${triggeredItem.newTriggeredConditionValue}`;
      if (triggeredItem.newTriggeredConditionValue === undefined) {
        newState.newTriggeredConditionValue = '';
      }
      newState.timer = `${triggeredItem.timer}`;
      if (triggeredItem.timer === undefined) {
        newState.timer = '';
      }
      this.setState(newState);
      this.updateValidity(newState);
    }
  };

  onFieldFocus = () => {
    if (!this.props.isEditingItem) {
      this.props.startEditing();
    }
  };

  handleChange = (event: any) => {
    if (!this.props.isEditingItem) {
      this.props.startEditing();
    }
    const value = event.target.value;
    const fieldName = event.target.id;
    this.setState({ [fieldName]: value, hasChanges: true });
    const newVal = { ...this.state };
    newVal[fieldName] = value;
    this.updateValidity(newVal);
  };

  updateValidity = (newVal: {
    id: string,
    condition?: string,
    conditionValue?: string,
    timer?: string,
    newTriggeredCondition?: string,
    newTriggeredConditionValue?: string,
    disableNotif?: boolean,
  }) => {
    let isValid = false;
    if (newVal) {
      const { id } = newVal;
      isValid = !!id;
    }
    this.setState({ isValid });
  };

  getDataToSave = (props?: TriggeredItemInputProps = this.props, state?: State = this.state) => {
    const {
      id,
      timer,
      condition,
      conditionValue,
      newTriggeredCondition,
      newTriggeredConditionValue,
      disableNotif,
    } = state;
    const { updateTrigger, triggeredItem, parentId } = props;

    const newTrigger = new TriggeredItem(triggeredItem);
    newTrigger.id = id;
    newTrigger.timer = timer && timer.length ? parseInt(timer, 10) : undefined;
    newTrigger.condition = condition;
    newTrigger.conditionValue = conditionValue && conditionValue.length ? parseInt(conditionValue, 10) : undefined;
    newTrigger.newTriggeredCondition = newTriggeredCondition;
    newTrigger.newTriggeredConditionValue = newTriggeredConditionValue && newTriggeredConditionValue.length
      ? parseInt(newTriggeredConditionValue, 10)
      : undefined;
    newTrigger.disableNotif = !!disableNotif;
    return { newTrigger, updateTrigger, parentId };
  };

  updateWithData = async (updateData, notifyUi: boolean = false) => {
    const { updateTrigger, parentId, newTrigger } = updateData;
    if (updateTrigger) {
      updateTrigger(parentId, newTrigger);
      if (notifyUi) {
        this.setState({ hasChanges: false });
      }
    }
  };

  updateTrigger = () => {
    const updateData = this.getDataToSave();
    this.updateWithData(updateData, true);
  };

  render() {
    const {
      screenplayEngineParent, screenplayEngineChild, items, childType, parentType, t,
    } = this.props;
    const {
      id,
      timer,
      condition,
      conditionValue,
      newTriggeredCondition,
      newTriggeredConditionValue,
      disableNotif,
      isValid,
      hasChanges,
    } = this.state;

    const saveBtnClass = hasChanges ? 'btn-warning' : 'btn-outline-secondary';
    return (
      <div className="card bg-light screenBlock" style={{ height: '100%', overflow: 'hidden' }}>
        <div className="card-header">
          <h5 style={{ marginBottom: 0 }}>{t(['screens.scenarioEdition.triggerEdition.sectionTitle', ''])}</h5>
        </div>
        <div className="pr-3 ml-3 mt-3" style={{ height: '100%', overflow: 'scroll' }}>
          <div className="form-group">
            <label htmlFor={'id'}>{t(['screens.scenarioEdition.triggerEdition.item', ''])}</label>
            {
              <select
                className="custom-select"
                id="id"
                onFocus={this.onFieldFocus}
                onChange={this.handleChange}
                value={id}
              >
                <option value="">{t(['general.select', ''])}</option>
                {items
                  && items.map(element => (
                    <option key={element.id} value={element.id}>
                      {element.id}
                    </option>
                  ))}
              </select>
            }
            <div className="form-group">
              <label htmlFor={'timer'}>{t(['screens.scenarioEdition.triggerEdition.trigger', ''])}</label>
              <input
                type="number"
                onFocus={this.onFieldFocus}
                className="form-control"
                placeholder="Timer"
                aria-label="Timer"
                id="timer"
                aria-describedby="basic-addon1"
                onChange={this.handleChange}
                value={timer}
              />
            </div>

            <div className="form-group">
              <label htmlFor={'condition'}>
                {`${t(['screens.scenarioEdition.triggerEdition.parentState', ''])} `}
                <HelpButton
                  helpStrings={screenplayEngineParent}
                  id={`screenplay${parentType}`}
                  title={`State help: ${parentType}`}
                  display={'inline'}
                  className={'ml-auto btn btn-outline-warning'}
                />
              </label>
              <select
                className="custom-select"
                id="condition"
                onFocus={this.onFieldFocus}
                onChange={this.handleChange}
                value={condition}
              >
                <option value="ezdezdez">{t(['general.select', ''])}</option>
                {screenplayEngineParent
                  && screenplayEngineParent.map(element => (
                    <option key={element.key} value={element.key}>
                      {element.key}
                    </option>
                  ))}
              </select>

              {condition === 'Unlocked' && (
                <div className="input-group">
                  <input
                    type="number"
                    onFocus={this.onFieldFocus}
                    className="form-control"
                    placeholder={t(['screens.scenarioEdition.triggerEdition.parentValue', ''])}
                    aria-label="conditionValue"
                    id="conditionValue"
                    aria-describedby="basic-addon1"
                    onChange={this.handleChange}
                    value={conditionValue}
                  />
                  <HelpButton
                    helpStrings={t(['helpStrings:general.triggerCondition', ''], { returnObjects: true })}
                    id={'conditionValueHelp'}
                    title={'conditionValue'}
                    display={'inline'}
                    className={'input-group-append btn btn-outline-warning ml-2'}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor={'newTriggeredCondition'}>
                {`${t(['screens.scenarioEdition.triggerEdition.newState', ''])} `}
                <HelpButton
                  helpStrings={screenplayEngineChild}
                  id={`screenplay${childType}`}
                  title={`State help: ${childType}`}
                  display={'inline'}
                  className={'ml-auto btn btn-outline-warning'}
                />
              </label>
              <select
                className="form-control"
                onFocus={this.onFieldFocus}
                id="newTriggeredCondition"
                onChange={this.handleChange}
                value={newTriggeredCondition}
              >
                {screenplayEngineChild
                  && screenplayEngineChild.map(element => (
                    <option key={element.key} value={element.key}>
                      {element.key}
                    </option>
                  ))}
              </select>
              {newTriggeredCondition === 'Unlocked' && (
                <div className="input-group">
                  <input
                    type="number"
                    onFocus={this.onFieldFocus}
                    className="form-control"
                    placeholder={t(['screens.scenarioEdition.triggerEdition.newValue', ''])}
                    aria-label="newTriggeredConditionValue"
                    id="newTriggeredConditionValue"
                    aria-describedby="basic-addon1"
                    onChange={this.handleChange}
                    value={newTriggeredConditionValue}
                  />
                  <HelpButton
                    helpStrings={t(['helpStrings:general.triggerCondition', ''], { returnObjects: true })}
                    id={'newTriggeredConditionValueHelp'}
                    title={'newTriggeredConditionValue'}
                    display={'inline'}
                    className={'input-group-append btn btn-outline-warning ml-2'}
                  />
                </div>
              )}
            </div>
            <InputBoolean
              fieldName="disableNotif"
              onFocus={this.onFieldFocus}
              value={disableNotif}
              label={t(['screens.scenarioEdition.triggerEdition.disableNotif', ''])}
              handleChange={this.handleChange}
            />
          </div>
          <div className="input-group-append">
            <div
              className={`mb-3 ${saveBtnClass} save-btn interactive`}
              id="button-addon2"
              onClick={this.updateTrigger}
              disabled={!isValid}
            >
              <FontAwesomeIcon icon={['fad', 'save']} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { parentId, childId, nodeId } = ownProps;
  let trigger;
  const parentItem = parentId && state.scenario.items[parentId];
  if (parentItem && childId) {
    trigger = parentItem.triggeredItems.find(trig => trig.nodeId === nodeId);
  }
  const itemKeys = Object.keys(state.scenario.items).filter(
    key => key !== undefined && key !== '__detachedNodes' && key !== '_persist',
  );
  const items = itemKeys.map(key => state.scenario.items[key]);
  const childItem = trigger && state.scenario.items[trigger.id];
  const childTriggers = childItem && childItem.type && state.configuration.screenplay[childItem.type];
  const parentTriggers = parentItem && parentItem.type && state.configuration.screenplay[parentItem.type];
  return {
    scenarioId: state.scenario.header.id,
    triggers: state.configuration.triggers,
    items,
    triggeredItem: trigger,
    parentType: parentItem && parentItem.type,
    childType: childItem && childItem.type,
    screenplayEngineParent: parentTriggers || [],
    screenplayEngineChild: childTriggers || [],
    locale: state.preferences.editionLocale,
  };
};

const mapDispatchToProps = {
  updateTrigger: ItemsServiceHelper.updateTriggeredItem,
  addNotif: EventsServiceHelper.addNotif,
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withTranslation(['default', 'helpStrings']),
)(TriggeredItemInput);
